import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';

import type { CatalogApp } from '../api/queries';
import { useApps, useProfile } from '../api/queries';
import { appColumns } from '../components/appColumns';
import { AppDetailDialog } from '../components/AppDetailDialog';
import { listGridSx } from '../components/listGridSx';
import { RequestAccessDialog } from '../components/RequestAccessDialog';

type Ownership = 'all' | 'available' | 'mine';

export default function Browse() {
  const { data, isLoading, error } = useApps();
  const { data: profile } = useProfile();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [ownership, setOwnership] = useState<Ownership>('all');
  const [detailApp, setDetailApp] = useState<CatalogApp | null>(null);
  const [requestApp, setRequestApp] = useState<string | null>(null);

  const owned = useMemo(() => new Set(profile?.apps ?? []), [profile?.apps]);
  const inUse = useMemo(() => (data ?? []).filter((a) => a.in_use), [data]);
  const categories = useMemo(
    () =>
      [...new Set(inUse.map((a) => a.category ?? 'Uncategorized'))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [inUse],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inUse.filter((a) => {
      if (ownership === 'mine' && !owned.has(a.app_name)) return false;
      if (ownership === 'available' && owned.has(a.app_name)) return false;
      if (category && (a.category ?? 'Uncategorized') !== category) return false;
      if (q && !`${a.display_name ?? ''} ${a.app_name} ${a.vendor ?? ''}`.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [inUse, search, category, ownership, owned]);
  // Count owned apps the same way the "My apps" toggle filters them (owned ∩
  // visible catalog) so the header number always matches the toggle's rows.
  const ownedVisible = useMemo(
    () => inUse.filter((a) => owned.has(a.app_name)).length,
    [inUse, owned],
  );

  const columns = useMemo(() => appColumns(owned), [owned]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load apps: {String(error)}</Alert>;

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" component="h1" gutterBottom>
          Browse
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {inUse.length} apps in use across {categories.length} categories
          {ownedVisible > 0 && ` · you have ${ownedVisible}`}
        </Typography>
      </div>

      {profile?.site && (
        <Alert severity="info">
          You&rsquo;re seeing the apps available for your role. Need something else? Contact
          Corporate IT.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search apps or vendors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 360, maxWidth: '100%' }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={category ?? ''}
          onChange={(e) => setCategory(e.target.value === '' ? null : e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All ({inUse.length})</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {c} ({inUse.filter((a) => (a.category ?? 'Uncategorized') === c).length})
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={ownership}
          onChange={(_e, v: Ownership | null) => v && setOwnership(v)}
          aria-label="ownership filter"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="available">Available</ToggleButton>
          <ToggleButton value="mine">My apps</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <DataGrid
        rows={visible}
        columns={columns}
        getRowId={(r) => r.app_name}
        getRowHeight={() => 'auto'}
        onRowClick={(params) => setDetailApp(params.row)}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
          sorting: { sortModel: [{ field: 'app_name', sort: 'asc' }] },
        }}
        sx={listGridSx}
      />

      <AppDetailDialog
        app={detailApp}
        myApps={profile?.apps ?? []}
        onClose={() => setDetailApp(null)}
        onRequestAccess={(app) => {
          setDetailApp(null);
          setRequestApp(app.app_name);
        }}
      />
      <RequestAccessDialog appName={requestApp} onClose={() => setRequestApp(null)} />
    </Stack>
  );
}
